'use client';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

const EmailVerificationBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner should be shown
  const shouldShowBanner = 
    isAuthenticated && 
    user && 
    !user?.email_confirmed_at && 
    !isDismissed;

  // Load dismissed state from localStorage on mount
  useEffect(() => {
    if (isAuthenticated && user && !user?.email_confirmed_at) {
      const dismissed = localStorage.getItem('email-verification-banner-dismissed');
      setIsDismissed(dismissed === 'true');
    }
  }, [isAuthenticated, user]);

  // Reset dismissed state when email gets verified
  useEffect(() => {
    if (user?.email_confirmed_at) {
      localStorage.removeItem('email-verification-banner-dismissed');
      setIsDismissed(false);
    }
  }, [user?.email_confirmed_at]);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('email-verification-banner-dismissed', 'true');
  };

  if (!shouldShowBanner) {
    return null;
  }

  const verifyEmailUrl = `/verify-email?email=${encodeURIComponent(user?.email || '')}`;

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-1">
            {/* Warning Icon */}
            <svg
              className="w-5 h-5 text-yellow-600 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            
            {/* Message */}
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">Your email isn't verified yet.</span>{' '}
              Check your inbox or{' '}
              <Link
                href={verifyEmailUrl}
                className="underline hover:text-yellow-900 font-semibold"
              >
                resend the verification email →
              </Link>
            </p>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="text-yellow-600 hover:text-yellow-800 transition-colors flex-shrink-0"
            aria-label="Dismiss banner"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;